// Breathing phase label for the Buddha Game.
// Shows the current phase (Inhale, Hold, Exhale) with smooth fade transitions.

import { useEffect, useState } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type BreathingPhase =
  | 'inhale'
  | 'holdAfterInhale'
  | 'exhale'
  | 'holdAfterExhale';

interface BreathPhaseLabelProps {
  /** Current phase of the breathing cycle */
  currentPhase: BreathingPhase;
  /** Whether the breathing system is active */
  isActive: boolean;
}

// ============================================================================
// PHASE LABELS
// ============================================================================

// We intentionally remove visible text for inhale/exhale phases so the UI
// doesn't display the large 'INHALE'/'EXHALE' text. Hold phases still show
// an informative label.
const PHASE_LABELS: Record<BreathingPhase, string> = {
  inhale: '',
  holdAfterInhale: 'Hold',
  exhale: '',
  holdAfterExhale: 'Hold',
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function BreathPhaseLabel({
  currentPhase,
  isActive,
}: BreathPhaseLabelProps) {
  const [displayPhase, setDisplayPhase] =
    useState<BreathingPhase>(currentPhase);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Handle phase transitions with fade effect
  useEffect(() => {
    if (currentPhase !== displayPhase) {
      // Start fade out
      setIsTransitioning(true);

      // After fade out, update the label and fade back in
      const timeout = setTimeout(() => {
        setDisplayPhase(currentPhase);
        setIsTransitioning(false);
      }, 150); // Half of the transition duration

      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [currentPhase, displayPhase]);

  const label = PHASE_LABELS[displayPhase];

  // If there is no label (we intentionally hide inhale/exhale text),
  // don't render the pill background to avoid showing an empty UI element.
  return (
    <div
      className="flex items-center justify-center"
      style={{
        minHeight: 32,
      }}
    >
      {label ? (
        <span
          className="rounded-full px-5 py-2 text-xl font-medium uppercase tracking-widest"
          style={{
            color: 'rgba(255, 255, 255, 0.95)',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
            opacity: isActive ? (isTransitioning ? 0 : 1) : 0.5,
            transition: 'opacity 0.2s ease',
            letterSpacing: '0.15em',
            backdropFilter: 'blur(4px)',
          }}
        >
          {label}
        </span>
      ) : null}
    </div>
  );
}
